<template>
<div class="chapter">
<div class="inner_page">
<div class="section" id="ch01">
<h2 class="calibre9" id="calibre_pb_1">Chapter I. <br class="calibre13"/> The Hackleton Case</h2>
<p class="calibre14">Neville Shandon stood at the window of his brother’s study
gazing contentedly out over the Whistlefield grounds. This was
a good place to recuperate in, he reflected, especially when one
could only snatch a couple of days at a time from the grinding
pressure of a barrister’s practice. His eye travelled slowly over
the prospect of greenery which lay before him, lawn beyond
lawn, down to where a glint of silver showed where the river
cut across the estate. Beyond that came the stretches of the
Low Meadows, intersected here and there by the darker green
of the hedges; then the long curve of the main road; and at
last, closing the horizon, the gentle slope of Longshoot Hill
surmounted by its church spire. A bee hummed lazily at the
open window; then, startled by a movement, it shot away,
the note of its wings growing higher and fainter as it receded
in the sunlight. The King’s Counsel let his attention wander
for a moment to the rooks sailing, in their effortless flight
around the tree-crests by the river; then, with something more
than apparent reluctance, he turned away from the landscape.</p>
<p class="calibre15">“You did pretty well when you bought Whistlefield, Roger,”
he commented as he moved back into the room. “It’s as
restful a place as I know. If it weren’t that I can get down here
from time to time, I’d be hard put to it to keep fit for my work.
Think of the Law Courts on a day like this! And that Hackleton
case has been a bit of a strain, a bigger business than usual.”</p>
<p class="calibre15">His twin brother nodded a general assent, but made no
audible reply. There was more than the normal family
resemblance between the two men. In height and build they
were much alike; both were grey-haired and clean-shaven; and
even the hard lines at the corners of the barrister’s mouth
found their counterparts in the deeply-chiselled curves which
made Roger Shandon’s face a slightly forbidding one. Whether
deliberately or not, the twins accentuated their physical
resemblance by a similarity in their dress.</p>
<p class="calibre15">“We have the same tailor,” Roger once explained. “When I
go to him, I say: ‘Make me a suit like my brother’s last one.’ I
believe Neville says the same. The fellow has our measurements,
so there’s no more needed on that visit. Neville and I have much
the same taste in shades, so it generally comes out all right.”</p>
<p class="calibre15">The likeness between the twins went even deeper than the
surface. Both owed their success in life to a certain hardness of
character coupled with an abundance of energy. Neville, going
to the Bar, had made himself feared from the first as a brutal
and domineering cross-examiner; and his criminal practice had
done little to soften his professional manners. Roger’s rise to
prosperity had been more mysterious. It was vaguely known
that he had made money in South Africa and South America;
but the exact methods which had led to his fortune were never
discussed by him. He had come home at the age of forty-five to
find his brother one of the leading lights of the Bar. The
purchase of the little Whistlefield estate had followed, and Roger
had apparently been content to settle down in the countryside
and make a clean break with the interests of his past.</p>
<p class="calibre15">The third brother, Ernest, seemed hardly to belong to the
same family as the twins. Though five years younger, he
had none of the vitality and energy which were so manifest
in his elders; and the contrast was accentuated by the weakness
of his eyes, which gazed incuriously at the world from behind
the concave lenses of his pince-nez. Left to fend for himself
by the time he was twenty, and with a couple of hundred
a year of his own, he had simply vegetated without even
attempting to go into any business; and when his brothers
had made their fortunes, he had slipped into the role of
parasite without a thought, had transferred himself to
Whistlefield, and had continued to live there ever since. Roger had
fallen into the habit of giving him a fluctuating allowance,
which he eked out as best he could by betting on a small scale.</p>
<p class="calibre15">“What’s this Hackleton case that you were talking about?”
he inquired with a certain dull interest.</p>
<p class="calibre15">Neville looked at his brother with an expression half
quizzical and half contemptuous. For days the Hackleton case
had extended in sordid detail over a good many columns of
most daily newspapers, for its intricacy had been enlivened by
frequent dramatic interchanges between witnesses and counsel.
It had shown Neville Shandon at his best, relentlessly driving
the defendants into one damaging admission after another.</p>
<p class="calibre15">“Do you never read newspapers, Ernest?” the barrister
demanded, quite unnettled by his brother’s ignorance of one
of the greatest cases in which he himself had taken a leading
part. Ernest’s interests were limited, as Neville knew; and
it was useless to expect him to go outside his normal range
merely from family concern. Wide-ranging curiosity was the
last quality one could expect from him.</p>
<p class="calibre15">Ernest blinked, took off his glasses and cleaned them, then
replaced them carefully before replying.</p>
<p class="calibre15">“No. At least, not all of them. (Confound these glasses,
they won’t grip my nose to-day, somehow. This is the fifth
time they’ve fallen off.) I often look at the newspapers,
Neville. I glance through the sporting news every day. I
never read the law column, though. I can’t understand it,
usually; and when I do understand it, it seems so damned
dull. At least, it’s dull to me; so I don’t look at it, usually.”</p>
<p class="calibre15">The barrister shrugged his shoulders slightly. He was above
petty vanity, and he felt no sting from his brother’s lack of
interest in his work.</p>
<p class="calibre15">“Just as well you left the Hackleton case alone, then,” he said.
“It’s an infernal tangle. It’s taken me months of work to see my
way through it; and if I happened to break down before it
comes to a finish, I doubt if a junior could take it on with
anything like success. But I think this week will see the end of it.”</p>
<p class="calibre15">Roger had listened to the dialogue without moving a muscle.
Ernest’s complete incuriosity was no surprise to him. He
could almost have predicted it. The youngest brother had
never had the slightest interest in anything which did not
touch himself. Family triumphs meant nothing to him, except
that indirectly they contributed to his welfare.</p>
<p class="calibre15">The barrister moved again to the window and looked out over
the landscape. A cloud of rooks caught his eye, sailing together
and then breaking up into a mass of wheeling individuals.</p>
<p class="calibre15">“After this sort of thing, the very thought of the air in the
Law Courts makes one sick,” he said at last.</p>
<p class="calibre15">“Hackleton’s coming up for the rest of your cross-examination
the day after to-morrow, isn’t he?” Roger asked.</p>
<p class="calibre15">“Yes. He’s a clever devil—sees a concealed point as well
as I do myself, and generally manages to skate round it more
or less. He’s just scraped through, so far; but I’ll have him
yet. It’ll be a bad business for him if he makes a slip. This
civil suit for breach of contract is only a preliminary canter,
if things turn out as I expect. One single breach in his case, and
the Public Prosecutor will be down on Hackleton instanter.
There’s ever so much in the background which we can’t bring
to light in this particular suit, but it would all come out if the
thing were to be transferred to the Criminal Court. Then we
could really get to the bottom of the business.”</p>
<p class="calibre15">“So I gathered, by reading the case. Anyone could see that
there was a lot in the background that you couldn’t touch on.”</p>
<p class="calibre15">“Once it all comes out, it’ll be the end of Hackleton. Five
years penal is the least he could look forward to. Pleasant
prospect for a man who lives on champagne. He’s an amazing fellow:
drinks like a fish and yet has almost as good a brain as I have.”</p>
<p class="calibre15">“And you think you’ll get him? Does he realise that?”</p>
<p class="calibre15">“I expect he does.”</p>
<p class="calibre15">“From all I’ve heard of him he hasn’t much to boast of
in the way of scruples. He started his career by speculations
in coffin ships, didn’t he? I seem to remember some trouble
with the insurance companies in more than one case.”</p>
<p class="calibre15">The barrister nodded:</p>
<p class="calibre15">“Constructive murder, simply. But that would be a trifle to
Hackleton. He’d do anything for money.”</p>
<p class="calibre15">Roger seemed to turn this over in his mind for a moment
or two before he spoke again.</p>
<p class="calibre15">“If he’s as hard a case as all that, I think I’d put on my
considering-cap if I were in your shoes, Neville. It seems to me
that you’re the weak joint in the harness.”</p>
<p class="calibre15">“I? How do you make that out? I’ve got this case at my
finger-ends, I tell you. No one knows it inside out as I do.”</p>
<p class="calibre15">“That’s precisely what I mean. Suppose he loosed a gang of
roughs on you before this cross-examination comes off? A good
sand-bagging would put you out of action for just the time
necessary to keep you out of the case; and that’s all he needs.
You say yourself that you have all the strings in your hands, and
I don’t suppose you’ve brought every card out of your sleeve
even for the benefit of your junior. It wouldn’t be like you if
you have. You were always one to keep a good deal in reserve.”</p>
<p class="calibre15">“That’s true enough,” Neville conceded with a grim smile.
“No one could handle Hackleton in just the way that I shall
this week. But I’m not particularly afraid of sand-bags or
that sort of thing. No one could tackle me here, so far as I
can see. One can’t do that kind of business in broad daylight
on the Whistlefield lawns. And there won’t be much chance of
getting at me on the way up to town or in London itself. I
quite admit the possibility of the thing when one’s dealing
with Hackleton. It’s quite on the cards; and because it’s never
been done before, there’s no reason why it shouldn’t be done
sometimes. I’m not nervous, of course; but I’m not likely
to run any risks by going about much after dark until this
affair is squared up.”</p>
<p class="calibre15">Roger Shandon’s face reflected the grimness of his brother’s
smile.</p>
<p class="calibre15">“I quite understand what you feel about it. In fact, I’m
in much the same boat myself. That’s what turned my mind
to the possibility in your case.”</p>
<p class="calibre15">The barrister glanced at him keenly.</p>
<p class="calibre15">“Some more of your disreputable past cropping up, eh?
I don’t care much for some of your old acquaintances.
Who’s this fresh one?”</p>
<p class="calibre15">Roger grinned shamelessly. His brother knew something of
the way in which he had made his money; for at times it had
been useful to Roger to take legal advice without bringing an
outsider into problems which came too near the edge of the law.</p>
<p class="calibre15">“It’s another gentleman with a grievance—from Cape Town
this time,” he explained. “He <em class="calibre16">says</em> he acted as my agent in
some I.D.B. business when I was out there. He <em class="calibre16">says</em> that I
got the profit out of it and that the profit was big enough to
split comfortably into two. According to him, I gave him
away to the authorities later on; and he spent a period of
retirement, on the Breakwater or some such health resort.
The cure took some years in the sanatorium; and he hated the
treatment. Too much open-air exercise with plain food; and
too many uniforms about for his taste. That part’s true
enough—he’s just out of gaol. As to the rest, he needn’t
expect me to corroborate it on oath.”</p>
<p class="calibre15">“Blackmail, I suppose?” asked the barrister, perfunctorily.
“I’ll have a talk with him, if you like. Perhaps my
persuasive style”—the harsh lines about his mouth deepened—“would
help to convert him to honesty. It’ll be no trouble.”</p>
<p class="calibre15">Roger nodded his thanks.</p>
<p class="calibre15">“I’ll turn you on if necessary; but it’s hardly likely. He
seems to me a vapouring sort of beast. ‘Your money or
your life’ style of thing, you know. When I naturally refused
point-blank to pay him a stiver, he frothed over at once with
threats to do me in. ‘Tim Costock, the Red-handed Avenger’—and
all that sort of thing. I left him frothing. He didn’t
seem to me the sort of type that would do more than froth—and
he can prove nothing.”</p>
<p class="calibre15">“I don’t suppose he can.” Neville agreed, knowing from
past experience that his brother left very little behind him
for enemies to pick up. “Well, I want to run over my notes
for the Hackleton case this afternoon. Where can I find a
place where I’ll be free from interruption? With these
youngsters in the house, one can never be sure of having a room
to oneself for half an hour at a time; and even if one retires
to one’s bedroom, somebody’s sure to start a duel with the
piano. I thought piano-playing had gone out of fashion; but
I’ve heard it every day since I came here.”</p>
<p class="calibre15">“That’s Arthur,” Roger Shandon interjected, irritably. “No
one else touches the damned thing.”</p>
<p class="calibre15">Ernest had apparently been cogitating deeply. He now
turned a dull eye on his elder brother.</p>
<p class="calibre15">“Try the Maze,” he advised.</p>
<p class="calibre15">“What do you mean by that?” demanded Neville. “Try
the Maze? It sounds like an advertisement for tea or one of
these riddles, like: ‘Why is a hen?’ ”</p>
<p class="calibre15">Ernest elaborated his suggestion.</p>
<p class="calibre15">“I mean the Maze,” he explained laboriously. “The thing
like the one at Hampton Court, down by the river, close to
the boat-house. None of the visitors is likely to find a way
to either of the centres; and none of us is likely to disturb
you. We don’t usually go there; at least, I don’t myself.”</p>
<p class="calibre15">Neville’s face had shown enlightenment at the first sentence.</p>
<p class="calibre15">“Oh, our Maze, you mean? We were talking about the
piano when you burst in, Ernest, and I didn’t quite take the
connection. That’s not a bad notion. As you say, nobody’s
likely to bother me if I plant myself in either of the centres.
Besides, I want all the fresh air I can get just now; it’ll be
better out there than anywhere inside the house. Right. I’ll
go to Helen’s Bower.”</p>
<p class="calibre15">He moved towards the door as he spoke; but before he
reached it a piano sounded not far off, and the opening bars
of Sinding’s <i class="calibre16" xml:lang="de">Frühlingsrauschen</i> came to their ears. Neville
turned back with his hand on the door-handle.</p>
<p class="calibre15">“By the way, Roger, what about that young nephew of
ours? He seems all right—a bit moody, perhaps, but nothing
out of the common. What does the doctor say?”</p>
<p class="calibre15">Roger’s face clouded.</p>
<p class="calibre15">“Arthur? He’s a young pest. About thrice a week he
takes a fancy to the piano, and then he spends the whole
day playing one piece over and over again, like an automatic
machine—except for the mistakes. Damnable. You don’t
know how I hate the sound of the <i class="calibre16">Spring Song</i> and
<i class="calibre16" xml:lang="de">Frühlingsrauschen</i>. You must have heard him at it this morning;
and now he’s starting all over again.”</p>
<p class="calibre15">The barrister nodded.</p>
<p class="calibre15">“Yes, but what about his general tone?” he asked. “Has he
got over the encephalitis completely? Did the Harley Street
man find anything permanently wrong?”</p>
<p class="calibre15">Roger’s face betrayed little satisfaction.</p>
<p class="calibre15">“Oh, the specialist looked devilish wise the last time he
examined him; but that was about all it amounted to. It
seems they know next to nothing about sleepy sickness. I
understood him to say that the brain cells are all churned
up with the inflammation; and the result may be anything you
please. Of course Arthur was lucky to get off with no physical
damage—his eyesight and hearing and all that are quite all
right. But it seems one can never tell what changes may have
taken place in the brain structure—things that don’t normally
show at all. He may be all right, for all one can tell. Or
again, he might turn into a homicidal maniac any day; and
then, as like as not, he’d go for the nearest relation handy.
A nice sort of fellow to have in one’s neighbourhood.”</p>
<p class="calibre15">The barrister evidently considered this prophecy exaggerated.</p>
<p class="calibre15">“He seems quite normal to me,” he said.</p>
<p class="calibre15">“Oh, I don’t worry much over him,” Roger admitted. “It’s
just that he’s got on my nerves so much that I can hardly
see him without snapping at him. I’ll have to get rid of him,
I think; send him on a sea-voyage or something of that sort.”</p>
<p class="calibre15">“Perhaps you get on his nerves, just the same way as he
gets on yours,” Ernest began in his low voice. “That’s what
usually happens. When one starts it, the other takes it up.
Usually that’s the way these things go. I shouldn’t wonder—hullo,
Sylvia! I didn’t expect you just yet; not for quite
a while. I’m not quite ready.”</p>
<p class="calibre15">A girl in her early twenties had come into the room and now
stood looking at her uncle with a fair pretence of indignation.</p>
<p class="calibre15">Sylvia Hawkhurst, the sister of the piano-playing Arthur,
had been left an orphan before she came of age; and as her
uncles were her trustees, she and her brother had been brought
to Whistlefield by Roger Shandon. She liked “to play at
house-keeping,” as she put it: and Roger soon learned that
she could run his small establishment better than any paid
housekeeper. Things went like clockwork after she had taken
command; and he soon realised that the secret of her
management was that everyone in the house adored her. One thing
she had set her face against: “We’ll have no men-servants, if
you please, uncle; at least, not in the house itself. I don’t
mind a chauffeur, of course. But I know what a girl can do,
and I’d prefer to keep within my limitations, if it’s all the
same to you.” Her uncle had let her have her way, and he
had never found any reason to complain of the results.</p>
<p class="calibre15">Sylvia’s housekeeping, however, occupied very little of her
time. She hunted in the season, drove her own car, played
tennis well and golf better still, and was reckoned one of the
best dancers in the neighbourhood. Most characteristic of all,
in spite of her looks, she was as popular with girls as with men.</p>
<p class="calibre15">As she came into the room, Ernest got out of his chair with
his usual deliberation and began a faintly shamefaced apology
for his unpreparedness; but she cut him short in mock irritation.</p>
<p class="calibre15">“He hasn’t even got his boots on!” she complained. “How
is it that I can run everything to time in this house except
you? Are you <em class="calibre16">ever</em> in time for anything, Uncle Ernest?”</p>
<p class="calibre15">“I always seem to have so much to do, Sylvia, usually. It’s
been a very busy day.”</p>
<p class="calibre15">The corners of Sylvia’s mouth quivered a little in spite of
her effort to look indignant.</p>
<p class="calibre15">“Very busy! I remember exactly what you did. You
played tennis for precisely thirty-five minutes this morning.
Then you organised a grand shooting tournament with the
air-guns and bored everyone stiff with it except Arthur, who
happens to be able to beat everyone else. Then you came
into the house; and I suppose you looked at the newspapers
till lunch. And since then, you’ve sat and smoked. You must
be dog-tired, poor thing. Do you think you could wrestle with
your boots now; or shall I have them brought here on a silver
salver and give you a hand with them myself? I’d rather not; so
if you can manage by yourself, I’ll go and bring the car round.
Put your watch in front of you and pinch yourself once a minute.
Then you won’t fall quite asleep. Do hurry up, uncle,” she
concluded, more seriously, “I want to get off as soon as I can.”</p>
<p class="calibre15">“Where are you taking him?” asked Roger.</p>
<p class="calibre15">“I’m going over to Stanningleigh village to do some
shopping first of all. Then I’m going to the Naylands to ask them
to come across and play tennis. When Uncle Ernest heard
that he begged me to take him along part of the way and
drop him at the East Gate, so that he could walk along the
main road to the bridge and have a look at the river.”</p>
<p class="calibre15">“I thought I’d like to see if it was worth fishing, just at
present,” Ernest added, in further explanation. “I’ve been
thinking about it for a day or two, but I’ve never found time,
somehow. Usually, just when I was starting out something
always seemed to come in the way. So to-day, since Sylvia
was going that way in the car anyhow, I thought. . .”</p>
<p class="calibre15">He broke off, observing Sylvia’s indignant eyes fastened
upon him.</p>
<p class="calibre15">“Boots!” she said, scathingly, and held the door open for
him to go out.</p>
<p class="calibre15">“I’ll be ready in a minute or two,” he assured her hastily
as he left the room.</p>
<p class="calibre15">“Men are a wonderful lot, aren’t they?” she said
confidentially to her two remaining uncles, as the door closed. “It
seems to me high time Uncle Ernest got married. He’s simply
incapable of looking after himself. You two are at least able
to cross the street for yourselves; but Uncle Ernest really gives
me a lot of worry. I think I saw a fresh wrinkle when I was
brushing my hair this morning.”</p>
<p class="calibre15">“I wondered what made you look peculiar at lunch-time,”
Neville admitted. “Now you mention it, I see it on your
brow. About as deep as this.”</p>
<p class="calibre15">He touched one of the deep-scored lines running down to
the side of his own mouth.</p>
<p class="calibre15">Sylvia laughed.</p>
<p class="calibre15">“You alarm me, uncle. I must have a look at the ravages
in a mirror before I venture out. Good-bye!”</p>
<p class="calibre15">She hurried out of the room. Neville looked at his watch.</p>
<p class="calibre15">“Time I was moving,” he said. “I think I’ll take Ernest’s
advice and try the Maze for seclusion. It’s hardly likely that
anyone will bother to go into it this afternoon; and I can’t
stand this piano-playing of Arthur’s. It grows irritating, as
you say. I’ll go now. But I must get my notes first.”</p>
<p class="calibre15">A thought seemed to strike Roger as the barrister opened
the door.</p>
<p class="calibre15">“I think I’ll try the Maze myself this afternoon. I feel a
bit sleepy; and it’s quiet in there. I shan’t disturb you. But
if it’s all the same to you, I’ll take Helen’s Bower myself. I’m
used to a chair there; it suits me. You can go to Narcissus’s
Pool instead. There’s nothing to pick and choose between
them, since they’re both in the Maze.”</p>
<p class="calibre15">“Very good,” the barrister agreed. “It’s all the same to
me, so long as no one interrupts me.”</p>
<p class="calibre15">He nodded abruptly and left the room.</p>
<p class="calibre15">When his brother had gone, Roger Shandon went over to
his writing-table and busied himself with some papers. The
distant piano seemed to have become more intrusive now that
he was left alone. It repeated <i class="calibre16" xml:lang="de">Frühlingsrauschen</i> with
brain-wearying persistence and a reiterated error in one particular
chord. Roger frowned irritably as he busied himself with the
documents before him, jotting down a note from time to
time on a scribbling-block.</p>
<p class="calibre15">“Damn that young whelp! I must talk to him about this.
One can’t concentrate one’s attention when half one’s mind’s
wondering if he’s going to make that same slip for the hundred
and first time.”</p>
<p class="calibre15">He continued his work for a few minutes, then rose and
rang the bell.</p>
<p class="calibre15">“Send Mr. Stenness, if you can find him,” he ordered when
the maid appeared.</p>
<p class="calibre15">In Ivor Stenness, Roger had secured an ideal private
secretary. Stenness not only had the efficiency of a machine, but he
possessed a full measure of qualities hardly less important.
If his employer was out of sorts, even the gruffest order failed
to ruffle the secretary’s temper. He was capable of taking
just the right amount of responsibility in emergencies without
ever going a hair’s breadth over the score. And his especial
recommendation in Roger’s eyes was that he could keep his
mouth shut. He never asked for explanations which might
have been difficult to give; and he never betrayed the slightest
surprise when, as sometimes happened, he opened threatening
letters.</p>
<p class="calibre15">“If I ever have a confession of murder to put on paper,”
Roger used to say, “Stenness will take it down in shorthand,
type it out, and get my signature, without turning a hair. So
far as he was concerned, it would be just a letter.”</p>
<p class="calibre15">Stenness’s other qualities were more in demand among the
remainder of the household. He had good natural manners;
and he could play games well enough to make him useful
where someone was often needed to make up a golf foursome
or a bridge table. A casual glance at him would have
suggested that he must employ a first-class valet; for his clothes
always looked new and he had the knack of carrying them well.</p>
<p class="calibre15">With all this, he was a perfectly safe person to have in a
house with a young girl. He was, somehow, too inhumanly
efficient to be attractive to girls younger than himself; and he
showed not the slightest desire to attract. Sylvia treated him
as a good friend, but she had dozens of friends whom she
treated in exactly the same fashion.</p>
<p class="calibre15">“Ah, Stenness!” Roger looked up as the secretary came in.
“I’ve gone over these letters and jotted down some notes. You
might get them off sometime to-day. There’s only one of
them that needs any explanation. Here it is. . . .”</p>
<p class="calibre15">Neville Shandon’s grim face appeared at the door for a
moment. In his hand was a sheaf of papers. Seeing his
brother engaged with the secretary, he nodded without saying
anything and closed the door behind him.</p>
<p class="calibre15">Roger continued his explanation of the matter in hand while
the secretary took a note or two. As the instructions ended,
the whirr of a car leaving the front of the house attracted
Roger’s attention and he crossed the room to look out of the
window. Sylvia was driving, and beside her was Ernest
Shandon. They glanced up as they passed under the study window,
and Sylvia waved her hand. Roger watched the car swing
sharply off the main avenue on its way to the East Gate, and
soon it vanished behind a belt of rhododendrons.</p>
<p class="calibre15">“They might have given Neville a lift,” Roger reflected as he
turned back into the room again. “They’ll be passing the
Maze on the road to the East Gate.”</p>
<p class="calibre15">The sound of the piano reasserted itself in the comparative
silence which followed the passing of the car. Roger made a
gesture of impatience.</p>
<p class="calibre15">“I suppose that’s my nephew playing?” he demanded.</p>
<p class="calibre15">“He was shooting darts at a target in the garden, a short
time ago,” Stenness explained, “but I think he came in a few
minutes ago.”</p>
<p class="calibre15">“It sounds like him. Since he had that attack of sleepy
sickness he always fumbles a bit on his chords—doesn’t seem
able to manage his fingers perfectly. That makes this din all
the harder to bear.”</p>
<p class="calibre15">Stenness refrained from any comment. Roger, after a
pause, continued irritably.</p>
<p class="calibre15">“Where are the visitors, Stenness? I wish they’d attract him
out of the house. Some days he’s all right and one never
sees him. Other days he sits and pounds that piano till one’s
head rings with it.”</p>
<p class="calibre15">“I noticed Miss Forrest and Mr. Torrance going towards
the rose garden a few minutes ago.”</p>
<p class="calibre15">Stenness confined himself to answering the direct question
and quietly ignored Roger’s exasperation. It was no business
of his to intervene in family squabbles.</p>
<p class="calibre15">“Well, that’s all I have for you at present, Stenness. As you’re
passing the door, send my nephew to me, will you? I must put
a stop to this nuisance. It’s gone on quite long enough.”</p>
<p class="calibre15">The secretary made a gesture of assent, then gathered up
his papers and left the room. A few seconds later, the
piano-playing stopped abruptly in the middle of a bar, and Roger’s
ear caught the clang of the keyboard lid being carelessly
slammed. After a moment or two, his nephew entered the study.</p>
<p class="calibre15">In order to give his irritation time to cool down, Roger
refrained from speaking immediately. He motioned his
nephew to sit down, whilst he himself pulled out his
cigar-case and became busy with the preparations for a smoke.
Having got his cigar well alight, he turned round.</p>
<p class="calibre15">“Must you hammer that piano for hours at a time, Arthur?
I hate to interfere with your simple pleasures, of course; but
the infernal din you make has had quite a long enough run.
You’ve played <i class="calibre16" xml:lang="de">Frühlingsrauschen</i> at least two dozen times
to-day; and that’s just twenty-four times oftener than I want
to hear it. You can cut it out of the bill, after this. In fact,
you can leave the piano alone, once for all. I’m sick of
hearing you play. You’re a nuisance to everyone, raising
Pandemonium at all hours of the day. Find some quieter
amusement, or clear out of the house.”</p>
<p class="calibre15">Arthur Hawkhurst’s eyebrows rose in mild surprise at his
uncle’s complaint.</p>
<p class="calibre15">“I’d no idea it worried you, uncle.”</p>
<p class="calibre15">“Well, drop it.”</p>
<p class="calibre15">“Perhaps I have been overdoing <i class="calibre16" xml:lang="de">Frühlingsrauschen</i> a bit. I
hadn’t thought of that. Somehow I never seem to get through
it without a mistake in one or two chords, and I want to
make a clean job of it, once at least.”</p>
<p class="calibre15">“I’ve got a pair of quite good ears. You needn’t think I
missed your mistakes. They make it more irritating, that’s all.”</p>
<p class="calibre15">Arthur hastened to admit his errors.</p>
<p class="calibre15">“Well, no more <i class="calibre16" xml:lang="de">Frühlingsrauschen</i>, then. What about the
<i class="calibre16" xml:lang="fr">Barcarolle</i>? Offenbach’s, I mean. Any objection to
that?”</p>
<p class="calibre15">“Yes. Will you be good enough to understand that you’re
not to bang on that piano again.”</p>
<p class="calibre15">“Oh, you mean it? I thought it was just your fun, uncle.
But I like the piano. Surely you’ll let me use it sometimes.”</p>
<p class="calibre15">“No. I’ve had enough of it.”</p>
<p class="calibre15">“But . . .”</p>
<p class="calibre15">Roger’s face had been darkening.</p>
<p class="calibre15">“That’s enough! I’ve more important things to talk to you
about. What age are you nowadays? Twenty-two or twenty-three,
isn’t it? And you’ve never done a stroke of work in
your life, so far? A pretty record, isn’t it?”</p>
<p class="calibre15">He paused, and paced over to the window and back again.</p>
<p class="calibre15">“That’s got to stop. I’ve had to support one loafer—your
Uncle Ernest. But if you imagine that I have a fad for
collecting loafers, you’re mistaken. I’ve got your uncle on
my hands permanently, I suppose; but I don’t propose to
increase my stock of parasites for your benefit. You’ll have
to find something to do. I’m not going to let you hang
around Whistlefield for ever.”</p>
<p class="calibre15">Arthur’s good-natured face had darkened in its turn.</p>
<p class="calibre15">“You might increase your stock of politeness without
overdoing things, it seems to me. I’m not altogether a loafer.
I’m an invalid.”</p>
<p class="calibre15">Roger took no notice of the plea.</p>
<p class="calibre15">“Whistlefield isn’t a hospital.”</p>
<p class="calibre15">“Or an asylum—I suppose that’s what you mean? You’d
better take care, uncle. There are some things a fellow doesn’t
forget, once they’re said.”</p>
<p class="calibre15">Roger’s temper, never very far below the surface, boiled
up at his nephew’s remark.</p>
<p class="calibre15">“That’s enough, Arthur. I’ll give you three months more.
After that, you can fend for yourself. You won’t starve.
You’ve got enough money to keep you alive even if the worst
comes to the worst. Anyhow, I wash my hands of you.”</p>
<p class="calibre15">Arthur Hawkhurst’s control was no better than his uncle’s
when once the point had penetrated through the skin.</p>
<p class="calibre15">“A pretty specimen of an uncle! The kind one meets in the
‘Babes in the Wood,’ eh? Go out into the world and starve,
Arthur dear. The little dicky-birds will put leaves on you—<em class="calibre16">and
I’ll get the money your mother left you!</em> That’s the scheme, I
suppose. It’s a wonder a thing like you is allowed to live.”</p>
<p class="calibre15">The flagrant absurdity of the charge checked Roger for a
moment. After all, the boy was off his balance. One shouldn’t
take him seriously.</p>
<p class="calibre15">“You’re an ass, Arthur!” was all he vouchsafed in reply.</p>
<p class="calibre15">But Arthur’s disturbed brain had tilted out of its normal
equilibrium, and his rage found vent in a wild threat as he
flung himself out of the room.</p>
<p class="calibre15">“I’ve a good mind to get in first myself; and do for you,
before you can do me any more harm. Look out for yourself!”</p>
<p class="calibre15">As the door slammed behind his nephew, Roger settled
himself back into his chair. Arthur’s outbreak had come as a
complete surprise. Since his illness, the boy had given the
impression that he merely needed a firm hand. He had loafed
about the house in a condition not far from melancholia; and
at first it had required steady pressure to bring him to take
any interest in normal affairs. Gradually he had improved
and had passed over into a state of cheerful irresponsibility.
And now, just as the specialists were taking an optimistic
view of the future, had come this collapse into something
which seemed little short of mania, absolutely without warning.</p>
<p class="calibre15">“I’ll have to get this looked into,” Roger reflected. “He’s
evidently not so far on the road to recovery as we thought.”</p>
<p class="calibre15">Arthur’s threat had left him completely indifferent. He had
almost forgotten it when he rose again from his chair. In itself it
seemed unimportant, merely some wild words flung out in a
brain-storm. He left the house and took the road to the Maze.</p>
<p class="calibre15">Stenness saw his figure pass into the belt of rhododendrons;
and as soon as it had disappeared, the secretary made his way to
Roger’s study. An ABC time table was on one of the shelves;
and Stenness, taking it down, began to study the times of trains.</p>
<p class="calibre15">“I can’t leave it later than that,” he said to himself at last.
“The next one wouldn’t get me into London in time for the
boat-train.”</p>
<p class="calibre15">His eye turned to the window and ranged over the lawns.</p>
<p class="calibre15">“Well, it’ll be a hard wrench to leave here, no matter what
happens. And I wish I saw to-night over and knew where I stand.”</p>
<p class="calibre15">He passed to a fresh line of thought.</p>
<p class="calibre15">“At the worst, nothing will matter much if I don’t pull it off.”</p>
<p class="calibre15">He replaced the ABC on its shelf and went up to his own
room. First locking the door, he began deliberately to pack
his razors and other toilette articles in an attaché case. When
he had completed this task, he glanced round the room.</p>
<p class="calibre15">“Nothing else? No, all the rest of the stuff is waiting for
me in London.”</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0003.vue?6d66ab96